<template>
  <geo-json-source :data="data" :source-id="props.sourceId">
    <layer-group :layers="layers" :before="props.before" :hide-layer="hideLayer" />
  </geo-json-source>
</template>

<script setup lang="ts">
import { MapMouseEvent, MapTouchEvent, SymbolLayerSpecification } from 'mapbox-gl'
import LocationDTO from '@/models/location'
import GeoJsonSource from '../mapbox/GeoJsonSource.vue'
import LayerGroup from '../mapbox/LayerGroup.vue'
import { useMap } from '../mapbox/context'
import { useFieldView } from '../FieldViewState'
import { computed, onMounted, onBeforeUnmount } from 'vue'
import { locationPinStyle } from './styles/location-pin'

const { map } = useMap()
const { fieldViewState: state, setSelectedLocation } = useFieldView()

const props = defineProps<{
  layerId: string
  sourceId: string
  showLocation: LocationDTO | null
  before?: string
  hideLayer?: boolean
}>()

onMounted(() => {
  map.value?.on('mousedown', props.layerId, allowDraggingIfEditingOne)
  map.value?.on('touchstart', props.layerId, onTouchStart)
})

onBeforeUnmount(() => {
  map.value?.off('mousedown', props.layerId, allowDraggingIfEditingOne)
  map.value?.off('touchstart', props.layerId, onTouchStart)
})

const data = computed(() => {
  const location = props.showLocation

  const features = location
    ? [
        {
          id: location.id ?? undefined,
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [location.longitude!, location.latitude!],
          },
          properties: {
            Name: location.location,
            Icon: location.icon,
            IconColor: location.locationStatusColorHexCode,
            OwnerId: location.ownerId,
          },
        },
      ]
    : []

  return {
    type: 'FeatureCollection',
    features,
  }
})

type MapInteractionEvent = MapMouseEvent | MapTouchEvent

const layers: SymbolLayerSpecification[] = [
  {
    id: props.layerId,
    source: props.sourceId,
    type: 'symbol',
    layout: {
      ...locationPinStyle.layout,
      'symbol-sort-key': 0,
      'icon-allow-overlap': true,
      'icon-size': 1.5,
      'text-radial-offset': 2,
      'text-allow-overlap': true,
      'text-optional': false,
      'text-size': {
        stops: [
          [15, 11],
          [15.5, 12],
          [17, 14],
        ],
      },
    },
    paint: {
      ...locationPinStyle.paint,
      'text-opacity': 1,
    },
  },
]

const onTouchStart = (e: MapTouchEvent) => {
  const isNotMultiTouch = e.points.length === 1
  if (isNotMultiTouch) {
    allowDraggingIfEditingOne(e)
  }
}

const allowDraggingIfEditingOne = (event: MapInteractionEvent) => {
  if (state.value.editMode) {
    event.preventDefault()

    if (event.type === 'mousedown') {
      map.value?.on('mousemove', updateSelectedLatLng)
      map.value?.once('mouseup', onUp)
    } else {
      map.value?.on('touchmove', updateSelectedLatLng)
      map.value?.once('touchend', onUp)
    }
  }
}

const updateSelectedLatLng = (e: MapInteractionEvent) => {
  if (!map.value || !state.value.selectedLocation) return
  map.value.getCanvas().style.cursor = 'grabbing'

  setSelectedLocation({
    ...state.value.selectedLocation,
    latitude: e.lngLat.lat,
    longitude: e.lngLat.lng,
  })
}

const onUp = (e: MapInteractionEvent) => {
  if (!map.value || !state.value.selectedLocation) return
  map.value.getCanvas().style.cursor = ''

  setSelectedLocation({
    ...state.value.selectedLocation,
    latitude: e.lngLat.lat,
    longitude: e.lngLat.lng,
  })

  map.value.off('mousemove', updateSelectedLatLng)
  map.value.off('touchmove', updateSelectedLatLng)
}
</script>
